import { useState } from 'react';
import styled from 'styled-components';

import Badge from 'components/shared/Badge';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import Workflow, { WorkflowAction, WorkflowGroupOptions, WorkflowLookupOption } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import CircleCheckIcon from 'svg/CircleCheckIcon';
import CircleXIconOutlined from 'svg/CircleXIconOutlined';

import { completionAttribution, sortedGroupsByName } from './FanoutPanel';
import { Pill, Tone } from './StatusBadge';

// The always-available Referral Outcome (OLIO-2611): Selected or Not Selected, and on Selected,
// which facility. The step sits quietly at Pending on the API side (never demands attention), but
// this panel is interactive the whole time — a coordinator records Not Selected the moment the
// patient chooses elsewhere. The facility list shows every targeted building, alpha ordered, each
// with its current facility-decision pill — informing the pick rather than filtering it
// (deliberately permissive; the API doesn't enforce it either).
//
// Not Selected carries why (OLIO-2736): a reason, then a competitor when the reason says one won
// it, then the competitor's name in free text when the one picked can't be reported on. Which
// reason owes a competitor and which competitor owes a name are declared BY THE LOOKUP ITEMS
// (`requiresCompetitor` / `requiresDetail`), never by slugs hardcoded here — a client shipping its
// own lists nominates its own triggers.
export const OUTCOME_SELECTED = 'selected';
export const OUTCOME_NOT_SELECTED = 'not_selected';

// The outcome was won/lost before OLIO-2736. Referrals decided then still hold those values, so
// every read normalizes rather than translating at each site.
const LEGACY_OUTCOMES: Record<string, string> = { won: OUTCOME_SELECTED, lost: OUTCOME_NOT_SELECTED };
export const normalizeOutcome = (value: unknown): string =>
  typeof value === 'string' ? (LEGACY_OUTCOMES[value] ?? value) : '';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: Record<string, string>) => void;
  isCompleting: boolean;
}

export default function ReferralOutcomePanel({ action, workflow, onComplete, isCompleting }: Props) {
  const [outcome, setOutcome] = useState<string | null>(null);
  const [facilityId, setFacilityId] = useState('');
  const [reason, setReason] = useState<WorkflowLookupOption | null>(null);
  const [competitor, setCompetitor] = useState<WorkflowLookupOption | null>(null);
  const [competitorName, setCompetitorName] = useState('');

  const facilities = sortedGroupsByName(workflow.groups);
  // Nothing to ask when the referral went to one building — it is the answer.
  const onlyFacility = facilities.length === 1 ? facilities[0] : undefined;
  const reasons = workflow.lookupOptions('not_selected_reasons');
  const competitors = workflow.lookupOptions('competitors');
  const needsCompetitor = !!reason?.requiresCompetitor;
  const needsCompetitorName = needsCompetitor && !!competitor?.requiresDetail;

  const decisionDisplay = (groupId: string): { label: string; tone: Tone } => {
    const value = workflow.valueFor('facility_determination', { groupId })?.value;
    if (value === 'accepted') return { label: 'Accepted', tone: 'success' };
    if (value === 'denied') return { label: 'Denied', tone: 'danger' };
    return { label: 'Pending', tone: 'neutral' };
  };

  const selectedFacilityId = onlyFacility?.id ?? facilityId;
  // Every requirement the API enforces, mirrored so Confirm is never a button that fails.
  const notSelectedReady =
    !!reason && (!needsCompetitor || !!competitor) && (!needsCompetitorName || !!competitorName.trim());
  const canSave =
    !isCompleting &&
    ((outcome === OUTCOME_NOT_SELECTED && notSelectedReady) || (outcome === OUTCOME_SELECTED && !!selectedFacilityId));

  const save = () => {
    if (!outcome) return;
    if (outcome === OUTCOME_SELECTED) {
      onComplete(action.id, { final_outcome: OUTCOME_SELECTED, selected_facility_id: selectedFacilityId });
      return;
    }
    onComplete(action.id, {
      final_outcome: OUTCOME_NOT_SELECTED,
      not_selected_reason: reason?.apiName ?? '',
      // Only what this reason actually calls for — the API drops contradictions, but sending them
      // would still be claiming something the person never chose.
      ...(needsCompetitor && competitor ? { competitor: competitor.apiName } : {}),
      ...(needsCompetitorName ? { competitor_name: competitorName.trim() } : {}),
    });
  };

  const chooseReason = (option: WorkflowLookupOption | null) => {
    setReason(option);
    // A reason that no longer asks for a competitor must not keep the last one's answer.
    if (!option?.requiresCompetitor) {
      setCompetitor(null);
      setCompetitorName('');
    }
  };

  return (
    <Wrap data-testid='referral-outcome-panel'>
      <BodySmall>
        Confirm the outcome of this referral below by indicating whether the patient selected your facility.
      </BodySmall>
      <Choices>
        <ChoiceButton
          type='button'
          $selected={outcome === OUTCOME_SELECTED}
          onClick={() => setOutcome(OUTCOME_SELECTED)}
          data-testid='outcome-won'>
          <IconWrap $color={colors.green100}>
            <CircleCheckIcon width={28} height={28} />
          </IconWrap>
          Selected
        </ChoiceButton>
        <ChoiceButton
          type='button'
          $selected={outcome === OUTCOME_NOT_SELECTED}
          onClick={() => setOutcome(OUTCOME_NOT_SELECTED)}
          data-testid='outcome-lost'>
          <IconWrap $color={colors.red100}>
            <CircleXIconOutlined width={28} height={28} />
          </IconWrap>
          Not Selected
        </ChoiceButton>
      </Choices>

      {outcome === OUTCOME_NOT_SELECTED && (
        <>
          <FacilityField>
            <BodySmall>Reason</BodySmall>
            <Select<WorkflowLookupOption>
              options={reasons}
              value={reason}
              onChange={chooseReason}
              getOptionValue={(option) => option.apiName}
              getOptionLabel={(option) => option.displayName}
              placeholder='Select a reason this referral was not selected'
              dataTestId='outcome-reason'
              menuPortalTarget={document.body}
              menuPlacement='auto'
              maxMenuHeight={240}
            />
          </FacilityField>

          {needsCompetitor && (
            <FacilityField>
              <BodySmall>Competitor</BodySmall>
              <Select<WorkflowLookupOption>
                options={competitors}
                value={competitor}
                onChange={(option) => {
                  setCompetitor(option);
                  if (!option?.requiresDetail) setCompetitorName('');
                }}
                getOptionValue={(option) => option.apiName}
                getOptionLabel={(option) => option.displayName}
                placeholder='Select competitor'
                dataTestId='outcome-competitor'
                menuPortalTarget={document.body}
                menuPlacement='auto'
                maxMenuHeight={240}
              />
            </FacilityField>
          )}

          {/* Free text because "Other" can't be reported on. What gets typed here is mined later for
              names that recur often enough to become real options. */}
          {needsCompetitorName && (
            <FacilityField>
              <BodySmall>Competitor name</BodySmall>
              <Input
                value={competitorName}
                onChange={(event) => setCompetitorName(event.target.value)}
                placeholder='Enter the competitor’s name'
                data-testid='outcome-competitor-name'
              />
            </FacilityField>
          )}
        </>
      )}

      {outcome === OUTCOME_SELECTED && !onlyFacility && (
        <FacilityField>
          <BodySmall>Select which facility the patient selected</BodySmall>
          <Select<WorkflowGroupOptions>
            options={facilities}
            value={facilities.find((group) => group.id === facilityId) ?? null}
            onChange={(group) => setFacilityId(group?.id ?? '')}
            getOptionValue={(group) => group.id}
            getOptionLabel={(group) => group.name}
            formatOptionLabel={(group, meta) => {
              if (meta?.context === 'value') return group.name;
              const display = decisionDisplay(group.id);
              return (
                <OptionRow>
                  {group.name}
                  <Pill $tone={display.tone}>{display.label}</Pill>
                </OptionRow>
              );
            }}
            placeholder={workflow.groups[0]?.groupType?.displayName ?? 'Select a facility'}
            dataTestId='outcome-facility'
            // The card clips overflow, so the open menu must portal out of it; near the
            // bottom of the page it flips upward instead of growing the document.
            menuPortalTarget={document.body}
            menuPlacement='auto'
            maxMenuHeight={240}
          />
        </FacilityField>
      )}

      <ConfirmRow>
        <Button disabled={!canSave} onClick={save} data-testid='outcome-save'>
          Confirm
        </Button>
      </ConfirmRow>
    </Wrap>
  );
}

// The completed state (Figma): the selected facility up front, and the ones that weren't behind a
// disclosure — a referral sent to five buildings shouldn't lead with four rows of "Not Selected".
// The recorded reason rides along, resolved through lookupLabel (which reads archived entries too,
// so an old decision still reads correctly after a list changes).
export function ReferralOutcomeResult({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const [showOthers, setShowOthers] = useState(false);
  const outcome = normalizeOutcome(workflow.valueFor('final_outcome')?.value);
  const winnerId = workflow.valueFor('selected_facility_id')?.value;
  const updated = completionAttribution(action);

  const reason = workflow.valueFor('not_selected_reason')?.value;
  const competitor = workflow.valueFor('competitor')?.value;
  const competitorName = workflow.valueFor('competitor_name')?.value;

  // Recorded under a vocabulary this panel can't render as facility rows (e.g. 'accepted' with no
  // winner) — show what was recorded rather than badging every building Not Selected.
  if (outcome !== OUTCOME_NOT_SELECTED && !(outcome === OUTCOME_SELECTED && winnerId)) {
    return (
      <ResultRows data-testid='referral-outcome-result'>
        <ResultRow>
          <ResultName>Recorded outcome: {outcome || 'unknown'}</ResultName>
          {updated && <ResultUpdated>{updated}</ResultUpdated>}
        </ResultRow>
      </ResultRows>
    );
  }

  const facilities = sortedGroupsByName(workflow.groups);
  const winner = facilities.find((group) => group.id === winnerId);
  const others = facilities.filter((group) => group.id !== winnerId);

  const reasonLine = () => {
    if (!reason || typeof reason !== 'string') return null;
    const label = workflow.lookupLabel('not_selected_reasons', reason) ?? reason;
    // The typed name when there is one: "Other" names the escape hatch, not the competitor.
    const who =
      typeof competitorName === 'string' && competitorName
        ? competitorName
        : typeof competitor === 'string' && competitor
          ? (workflow.lookupLabel('competitors', competitor) ?? competitor)
          : null;
    return (
      <ResultUpdated data-testid='outcome-reason-recorded'>
        {label}
        {who ? ` — ${who}` : ''}
      </ResultUpdated>
    );
  };

  const row = (name: string, groupId: string, selected: boolean) => (
    <ResultRow key={groupId} data-testid='outcome-row' data-group-id={groupId}>
      <ResultHeader>
        <ResultName>{name}</ResultName>
        <Badge color={selected ? 'green' : 'red'}>{selected ? 'Selected' : 'Not Selected'}</Badge>
      </ResultHeader>
      {updated && <ResultUpdated>{updated}</ResultUpdated>}
    </ResultRow>
  );

  return (
    <ResultRows data-testid='referral-outcome-result'>
      {winner && row(winner.name, winner.id, true)}
      {reasonLine()}

      {/* Not selected anywhere: every facility is in the same state, so there is no winner to lead
          with and nothing to hide behind a disclosure. */}
      {!winner && others.map((group) => row(group.name, group.id, false))}

      {winner && others.length > 0 && (
        <Disclosure>
          This referral was marked as “Not Selected” for {others.length}{' '}
          {others.length === 1 ? 'facility' : 'facilities'}.{' '}
          <DisclosureLink type='button' onClick={() => setShowOthers((open) => !open)} data-testid='toggle-others'>
            {showOthers ? 'Hide facilities' : 'View facilities'}
          </DisclosureLink>
        </Disclosure>
      )}
      {winner && showOthers && others.map((group) => row(group.name, group.id, false))}
    </ResultRows>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Choices = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ChoiceButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 24px;
  border-radius: 8px;
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black10)};
  background: ${({ $selected }) => ($selected ? colors.blue10 : colors.white)};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
`;

// Both icons draw with currentColor (the check via its fill default, the X via stroke).
const IconWrap = styled.span<{ $color: string }>`
  display: inline-flex;
  color: ${({ $color }) => $color};
`;

const ResultRows = styled.div`
  display: flex;
  flex-direction: column;
`;

const ResultRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ResultName = styled(BodySmallBold)`
  font-size: 14px;
`;

const ResultUpdated = styled(BodySmall)`
  color: ${colors.black50};
`;

const Disclosure = styled(BodySmall)`
  padding: 12px 0;
  color: ${colors.black75};
  border-top: 1px solid ${colors.black5};
`;

const DisclosureLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  cursor: pointer;
`;

const OptionRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ConfirmRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const FacilityField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 420px;
`;
